import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { TReviewSubmissionNote } from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { MdExpandLess, MdExpandMore, MdOutlineMail } from 'react-icons/md';

/** Short lists show in full; longer ones show the newest few first. */
const SHOW_FIRST = 3;

const RECIPIENT_STYLE: Record<TReviewSubmissionNote['recipient'], { label: string; bg: string; color: string }> = {
  teacher: { label: 'To teacher', bg: '#e8f1fd', color: '#1d5fb8' },
  student: { label: 'To student', bg: '#f3e8ff', color: '#6d28d9' },
};

/**
 * The notes admins sent about this submission, newest first: who sent it, to whom,
 * when, and what it said. A record for the teacher and the admins on the panel.
 */
export const SubmissionNotes: FC<{ notes: TReviewSubmissionNote[] }> = ({ notes }) => {
  const [isOpen, setIsOpen] = useState(false);
  if (notes.length === 0) {
    return null;
  }
  const shown = isOpen ? notes : notes.slice(0, SHOW_FIRST);

  return (
    <Paper variant="outlined" sx={{ mb: 2.5, borderRadius: '12px', overflow: 'clip', bgcolor: 'common.white' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <MdOutlineMail size={18} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Notes from admins ({notes.length})</Typography>
      </Box>
      <Box role="list" aria-label="Notes from admins">
        {shown.map(note => {
          const style = RECIPIENT_STYLE[note.recipient];
          const sent = DateTime.fromJSDate(new Date(note.sentAt));
          const from = note.from?.name ?? 'An admin';
          return (
            <Box
              role="listitem"
              key={note.id}
              sx={{
                display: 'flex',
                gap: 1.5,
                px: { xs: 2, md: 3 },
                py: 1.5,
                '& + &': { borderTop: '1px solid', borderColor: 'divider' },
              }}
            >
              <Avatar
                src={note.from?.avatar ? getMediaUrl(note.from.avatar) : undefined}
                sx={{ width: 32, height: 32, fontSize: '0.875rem' }}
              >
                {from.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, rowGap: 0.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {from}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${style.label} · ${note.to.name}`}
                    sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, bgcolor: style.bg, color: style.color }}
                  />
                  <Tooltip title={sent.toLocaleString(DateTime.DATETIME_MED)}>
                    <Typography variant="caption" color="text.secondary">
                      {sent.toRelative()}
                    </Typography>
                  </Tooltip>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                  {note.note}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      {notes.length > SHOW_FIRST && (
        <Box sx={{ px: { xs: 1, md: 2 }, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            size="small"
            onClick={() => setIsOpen(prev => !prev)}
            startIcon={isOpen ? <MdExpandLess /> : <MdExpandMore />}
            sx={{ textTransform: 'none' }}
          >
            {isOpen ? 'Show fewer' : `Show all ${notes.length} notes`}
          </Button>
        </Box>
      )}
    </Paper>
  );
};
